import { CHAT_TITLE_MAX } from '@server/types';
import { useRef, useState } from 'react';

import { Button } from '@/_shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/_shared/components/ui/dialog';
import { Input } from '@/_shared/components/ui/input';
import { Label } from '@/_shared/components/ui/label';

import { isChatGone } from '../lib/apply-chat-title';

interface RenameChatDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string | null;
  saving: boolean;
  onSave: (title: string) => Promise<unknown>;
}

/** Follows the audience rename modal: explicit Save, and preserve the draft on failure. */
export function RenameChatDialog({
  open,
  onOpenChange,
  title,
  saving,
  onSave,
}: RenameChatDialogProps): React.ReactElement {
  const submitting = useRef(false);
  return (
    <Dialog open={open} onOpenChange={(next) => !saving && !submitting.current && onOpenChange(next)}>
      <DialogContent closeDisabled={saving}>
        <DialogHeader>
          <DialogTitle>Rename chat</DialogTitle>
          <DialogDescription>Update the chat&apos;s name.</DialogDescription>
        </DialogHeader>
        {/* Radix unmounts closed content, so reopening always seeds from the latest title. */}
        <RenameForm
          title={title ?? ''}
          saving={saving}
          onCancel={() => onOpenChange(false)}
          onSave={async (next) => {
            if (saving || submitting.current) return;
            submitting.current = true;
            try {
              await onSave(next);
              onOpenChange(false);
            } catch (err) {
              // The mutation displays the error. Keep the draft open for a retry, unless the chat is
              // gone: a retry of that can never succeed, and a dialog left open says it might.
              if (isChatGone(err)) onOpenChange(false);
            } finally {
              submitting.current = false;
            }
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

function RenameForm({
  title,
  saving,
  onCancel,
  onSave,
}: {
  title: string;
  saving: boolean;
  onCancel: () => void;
  onSave: (title: string) => Promise<void>;
}): React.ReactElement {
  const [value, setValue] = useState(title);
  // The name the field was seeded with. `title` can move under an open dialog (the agent retitles
  // mid-turn), and comparing against the live prop would turn an unedited Save into a revert.
  const seeded = useRef(title);
  const trimmed = value.trim();
  // `maxLength` caps typing, not a seeded value — an agent-written title can arrive over the cap.
  const excess = trimmed.length - CHAT_TITLE_MAX;
  const disabled = saving || trimmed.length === 0 || excess > 0;
  const submit = (): void => {
    if (disabled) return;
    if (trimmed === seeded.current.trim()) {
      onCancel();
      return;
    }
    void onSave(trimmed);
  };
  return (
    <>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="chat-rename-name">Name</Label>
        <Input
          id="chat-rename-name"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          maxLength={CHAT_TITLE_MAX}
          placeholder="New conversation"
          disabled={saving}
          autoFocus
          aria-invalid={excess > 0}
          aria-describedby={excess > 0 ? 'chat-rename-error' : undefined}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
              e.preventDefault();
              submit();
            }
          }}
        />
        {excess > 0 && (
          <p id="chat-rename-error" className="text-xs text-danger" aria-live="polite">
            {`Remove ${excess} character${excess === 1 ? '' : 's'} — a name can be at most ${CHAT_TITLE_MAX} characters.`}
          </p>
        )}
      </div>
      <DialogFooter>
        <Button variant="secondary" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" loading={saving} disabled={disabled} onClick={submit}>
          Save
        </Button>
      </DialogFooter>
    </>
  );
}
